import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import { PARequestType, PAUnitType, THERAPY_ACUITIES, THERAPY_PERIOD_MAX_DAYS } from '../../../../dtos/patientmr/authorizations.dto';
import type { TherapyAcuity } from '../../../../dtos/patientmr/authorizations.dto';
import { isTherapyPaCategory } from './FormAssociationSection';
import { ATTENDANT_PROGRAM_ENTRIES } from '../../../../constants/disciplineRegistry';
import { AlertPill } from '../../../common/Pills';
import { SERVICE_CATEGORY_CONFIG, AuthorizationsService } from '../../../../services/patientmr/authorizations.service';

interface RequestSectionProps {
  serviceCategory: string;
  requestType: string;
  unitType: string;
  minutesPerUnit: number | null;
  requestedStartDate: string;
  requestedEndDate: string;
  revisionReason: string;
  /** Phase 20C: therapy PAs only */
  therapyAcuity?: string;
  /** Phase 20D: attendant PAs only */
  attendantProgram?: string;
  onChange: (field: string, value: string | number | null) => void;
  readOnly?: boolean;
  defaultOpen?: boolean;
}

const ACUITY_LABELS: Record<TherapyAcuity, string> = { ACUTE: 'Acute (up to 60 days)', CHRONIC: 'Chronic (up to 180 days)' };

/** Inclusive days between two YYYY-MM-DD dates, or null */
export const periodDays = (start: string, end: string): number | null => {
  const a = Date.parse(`${start}T00:00:00Z`);
  const b = Date.parse(`${end}T00:00:00Z`);
  return Number.isFinite(a) && Number.isFinite(b) ? Math.round((b - a) / 86400000) + 1 : null;
};

const SVC_OPTS = Object.entries(SERVICE_CATEGORY_CONFIG).map(([value, cfg]) => ({ value, label: cfg.label }));

const REQUEST_TYPE_OPTS = [
  { value: PARequestType.INITIAL, label: 'Initial / New' },
  { value: PARequestType.RECERTIFICATION, label: 'Recertification' },
  { value: PARequestType.REVISION, label: 'Revision' }
];

const UNIT_TYPE_OPTS = [
  { value: PAUnitType.UNITS, label: 'Units' },
  { value: PAUnitType.VISITS, label: 'Visits' },
  { value: PAUnitType.HOURS, label: 'Hours' }
];

export const RequestSection: React.FC<RequestSectionProps> = ({
  serviceCategory, requestType, unitType, minutesPerUnit, requestedStartDate, requestedEndDate,
  revisionReason, therapyAcuity = '', attendantProgram = '', onChange, readOnly = false, defaultOpen = true
}) => {
  const attendant = serviceCategory === 'attendant';
  const programLabel = (code: string) => ATTENDANT_PROGRAM_ENTRIES.find((p) => p.code === code)?.label || code;
  const [open, setOpen] = React.useState(defaultOpen);
  const therapy = isTherapyPaCategory(serviceCategory);
  const days = periodDays(requestedStartDate, requestedEndDate);
  const maxDays = therapyAcuity ? THERAPY_PERIOD_MAX_DAYS[therapyAcuity as TherapyAcuity] : null;
  const showMinPerUnit = unitType === PAUnitType.UNITS;

  const handleServiceCategoryChange = (value: string) => {
    onChange('serviceCategory', value);
    const cfg = SERVICE_CATEGORY_CONFIG[value];
    if (cfg) {
      onChange('unitType', cfg.defaultUnitType);
      onChange('minutesPerUnit', cfg.defaultMinutesPerUnit);
    }
  };

  if (readOnly) {
    return (
      <div className="border border-slate-200 rounded-lg overflow-hidden">
        <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50" type="button">
          <div className="flex items-center gap-2">
            {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
            <span className="text-sm font-semibold text-slate-800">Request Details</span>
            <AlertPill type="default" text="Read-only" />
          </div>
        </button>
        {open && (
          <div className="px-4 pb-4 border-t border-slate-100 pt-3">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Request Type</dt>
                <dd className="mt-0.5 text-sm text-slate-900">
                  {REQUEST_TYPE_OPTS.find(o => o.value === requestType)?.label || requestType}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Authorized In</dt>
                <dd className="mt-0.5 text-sm text-slate-900">
                  {UNIT_TYPE_OPTS.find(o => o.value === unitType)?.label || unitType}
                  {showMinPerUnit && minutesPerUnit && ` (${minutesPerUnit} min)`}
                </dd>
              </div>
              <div>
                <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Period</dt>
                <dd className="mt-0.5 text-sm text-slate-900">
                  {AuthorizationsService.formatDate(requestedStartDate)} &ndash; {AuthorizationsService.formatDate(requestedEndDate)}
                </dd>
              </div>
              {attendant && (
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Programme</dt>
                  <dd className="mt-0.5 text-sm text-slate-900" data-testid="pa-program-readonly">{attendantProgram ? programLabel(attendantProgram) : 'Not set'}</dd>
                </div>
              )}
              {therapy && (
                <div>
                  <dt className="text-xs font-medium text-slate-500 uppercase tracking-wider">Acuity</dt>
                  <dd className="mt-0.5 text-sm text-slate-900" data-testid="pa-acuity-readonly">
                    {therapyAcuity ? ACUITY_LABELS[therapyAcuity as TherapyAcuity] || therapyAcuity : 'Not set'}
                  </dd>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    );
  }

  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button onClick={() => setOpen(!open)} className="w-full flex items-center justify-between px-4 py-3 text-left bg-white hover:bg-slate-50" type="button">
        <div className="flex items-center gap-2">
          {open ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
          <span className="text-sm font-semibold text-slate-800">Request</span>
        </div>
      </button>
      {open && (
        <div className="px-4 pb-4 border-t border-slate-100 pt-3 space-y-4">
          <div className={`grid gap-4 ${showMinPerUnit ? 'grid-cols-4' : 'grid-cols-3'}`}>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Service Category <span className="text-red-400">*</span>
              </label>
              <select
                value={serviceCategory}
                onChange={e => handleServiceCategoryChange(e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {SVC_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Request Type <span className="text-red-400">*</span>
              </label>
              <select
                value={requestType}
                onChange={e => onChange('requestType', e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                {REQUEST_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Authorized In <span className="text-red-400">*</span>
              </label>
              <select
                value={unitType}
                onChange={e => {
                  onChange('unitType', e.target.value);
                  if (e.target.value === PAUnitType.UNITS) onChange('minutesPerUnit', 15);
                  else onChange('minutesPerUnit', null);
                }}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {UNIT_TYPE_OPTS.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">How the payer measures</p>
            </div>

            {showMinPerUnit && (
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                  Min per Unit <span className="text-red-400">*</span>
                </label>
                <input
                  type="number"
                  value={minutesPerUnit ?? 15}
                  onChange={e => onChange('minutesPerUnit', parseInt(e.target.value) || 15)}
                  min={1}
                  max={120}
                  className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
                />
                <p className="mt-1 text-xs text-slate-400">e.g., 15 for Medicaid PDN</p>
              </div>
            )}
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Start Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={requestedStartDate}
                onChange={e => onChange('requestedStartDate', e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                End Date <span className="text-red-400">*</span>
              </label>
              <input
                type="date"
                value={requestedEndDate}
                onChange={e => onChange('requestedEndDate', e.target.value)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-base focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors"
              />
              <p className="mt-1 text-xs text-slate-400">Max 120 days from start</p>
            </div>
          </div>

          {attendant && (
            <div>
              <label htmlFor="pa-attendant-program" className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Programme authorized by the MCO <span className="text-red-400">*</span>
              </label>
              <select
                id="pa-attendant-program"
                value={attendantProgram}
                onChange={e => onChange('attendantProgram', e.target.value || null)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {ATTENDANT_PROGRAM_ENTRIES.map(p => <option key={p.code} value={p.code}>{p.label} (T1019 {p.modifier})</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                The MCO assesses and authorizes attendant care. Record its authorization here; there is no PA packet.
              </p>
            </div>
          )}

          {therapy && (
            <div>
              <label htmlFor="pa-therapy-acuity" className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Acute or chronic <span className="text-red-400">*</span>
              </label>
              <select
                id="pa-therapy-acuity"
                value={therapyAcuity}
                onChange={e => onChange('therapyAcuity', e.target.value || null)}
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors appearance-none bg-white"
              >
                <option value="">Select...</option>
                {THERAPY_ACUITIES.map(a => <option key={a} value={a}>{ACUITY_LABELS[a]}</option>)}
              </select>
              <p className="mt-1 text-xs text-slate-400">
                Texas Medicaid: acute requests cover up to 60 days and bill modifier AT; chronic requests cover up to 180 days.
              </p>
              {maxDays !== null && days !== null && days > maxDays && (
                <p className="mt-1 text-xs text-red-600" data-testid="pa-acuity-period-error">
                  The requested period is {days} days; an {String(therapyAcuity).toLowerCase()} request covers at most {maxDays}.
                </p>
              )}
            </div>
          )}

          {requestType === PARequestType.REVISION && (
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1.5 text-left">
                Reason for Revision <span className="text-red-400">*</span>
              </label>
              <textarea
                rows={2}
                value={revisionReason}
                onChange={e => onChange('revisionReason', e.target.value)}
                placeholder="Briefly describe why..."
                className="w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-transparent transition-colors resize-none"
              />
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default RequestSection;
